import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, CircularProgress } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faBriefcase, faPlus, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import type { ICompaniesRepository, IPartnersRepository } from "App/Modules/Repositories";
import type { ICompany, IResponseWithLoading } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";

import { getUserProfile, hasUserPermissionAnywhere, SearchField } from "../../Shared";

import "./_companies.scss";
import { AddCompanyDialog } from "./AddCompanyDialog";

type CompaniesProps = {
    mmCompaniesRepository: ICompaniesRepository;
    mmPartnersRepository: IPartnersRepository;
};

export const Companies = (props: CompaniesProps) => {
    const { t } = useTranslation();
    const [globalFilter, setGlobalFilter] = useState("");
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [showAddCompany, setShowAddCompany] = useState(false);
    const [companies, setCompanies] = useState<IResponseWithLoading<ICompany[]>>(null);

    const profile = getUserProfile();

    useEffect(() => {
        const sub = props.mmCompaniesRepository.getAllCompanies().subscribe(
            (response: IResponseWithLoading<ICompany[]>) => {
                setCompanies(response);
                setLoadingCompanies(false);
            },
            (error) => {
                setCompanies(error);
                setLoadingCompanies(false);
            },
        );

        return () => {
            sub.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAddSuccess = (company: ICompany) => {
        setCompanies((current) => ({ ...current, data: [...current.data, company] }));
    };

    const filterFunc = (eq: ICompany) => {
        if (!globalFilter) {
            return true;
        }
        return (
            eq.name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1 ||
            eq.corporateIdNumber?.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1
        );
    };

    return (
        <div className="companies card">
            <h1 className="title title--h2">{t("BeforeLogin.Company.Companies")}</h1>
            <div className="companies__functions">
                <div className="companies__filter">
                    <SearchField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>
                {hasUserPermissionAnywhere(profile, Permission.PartnerOperations) ? (
                    <Button
                        onClick={() => {
                            setShowAddCompany(true);
                        }}
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<FontAwesomeIcon icon={faPlus} fixedWidth />}
                    >
                        {`${t("Gui.Buttons.Add")} ${t("BeforeLogin.Company.Company")}`}
                    </Button>
                ) : null}
            </div>

            {companies && companies.hasError ? (
                <div className="notice notice--critical">
                    <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                    <p className="caption">{t("Error.ErrorLoading")}</p>
                </div>
            ) : null}

            <div className="scrollable-list card card--nested card--alt">
                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                    <div className="entity-list">
                        {loadingCompanies ? (
                            <div className="entity-list__item entity-list__item--loading">
                                <CircularProgress />
                            </div>
                        ) : null}
                        {companies?.data?.filter(filterFunc).map((company) => (
                            <div className="entity-list__item" key={company.id}>
                                <a
                                    className="entity-list__item-link entity-list__item-link--padding"
                                    href={`#!/companies/${company.id}`}
                                >
                                    <div className="entity-list__item-icon entity-list__item-icon--alt">
                                        <FontAwesomeIcon icon={faBriefcase} />
                                    </div>
                                    <div className="entity-list__item-panel">
                                        <p className="paragraph paragraph--no-spacing">{company.name}</p>
                                        {company.corporateIdNumber ? (
                                            <p className="caption caption--no-spacing">{company.corporateIdNumber}</p>
                                        ) : null}
                                    </div>
                                    <div className="entity-list__item-arrow">
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </div>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Dialog open={showAddCompany} maxWidth="sm">
                <AddCompanyDialog
                    mmCompaniesRepository={props.mmCompaniesRepository}
                    mmPartnersRepository={props.mmPartnersRepository}
                    onAddSucces={onAddSuccess}
                    onClose={() => {
                        setShowAddCompany(false);
                    }}
                />
            </Dialog>
        </div>
    );
};
